// libs
import React, { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Plus } from '@lyearn/icons';
// components
import { Box, Button, Stack, Typography } from '@lyearn/ui';
import useListWithTempId from '@/hooks/useList/useListWithTempId';
import { ScorecardQuestionInput } from '@/types/schema';

// types
import { QuestionFormActions } from '../../interfaces';
import { getDefaultQuestionItem, isQuestionsEmpty, isQuestionTextEmpty } from '../../utils';
import QuestionItem from './components/QuestionItem';
// consts
import { QuestionsProps } from './types';

function Questions({ error, questions, onChange, setErrorElement }: QuestionsProps) {
  const { t } = useTranslation();

  const [questionList, actions] = useListWithTempId<ScorecardQuestionInput>(questions, onChange);
  const { push, onDelete, onDuplicate, onListInputChange } = actions;
  const questionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const errorItem = questionList.find((question) => isQuestionTextEmpty(question.text));
    if (errorItem) {
      setErrorElement(questionRefs.current[errorItem.tempId]);
    } else {
      setErrorElement(null);
    }
  }, [questionList]);

  const onQuestionItemAction = useCallback(
    (questionIndex: number) => (action: QuestionFormActions) => {
      switch (action) {
        case QuestionFormActions.Duplicate:
          onDuplicate(questionIndex);
          break;
        case QuestionFormActions.Delete:
          onDelete(questionIndex);
          break;
      }
    },
    [onDelete, onDuplicate],
  );

  const onButtonClick = useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      push(getDefaultQuestionItem(questionList.length + 1));
    },
    [push, questionList.length],
  );

  return (
    <Stack direction="col">
      {questionList.map((question, index) => (
        <Box key={question.tempId} ref={(el) => (questionRefs.current[question.tempId] = el)}>
          <QuestionItem
            key={question.tempId}
            error={error}
            question={question}
            onAction={onQuestionItemAction(index)}
            onChange={(item) => onListInputChange(item, index)}
          />
        </Box>
      ))}
      <Button
        className="mt-28 w-160"
        data-action={QuestionFormActions.Add}
        size="medium"
        startIcon={<Plus />}
        onClick={onButtonClick}>
        {t('Scorecard:AddQuestion')}
      </Button>
      {error && isQuestionsEmpty(questions) ? (
        <Typography className="mt-8 ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default Questions;
